<script setup>
import { onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import SLAMetrics from '../SLA/SLAMetrics.vue';
import SLATable from '../SLA/SLATable.vue';
import SLAReportFilters from '../SLA/SLAReportFilters.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  since: { type: Date, required: true },
  until: { type: Date, required: true },
});

const { t } = useI18n();
const store = useStore();

const slaReports = useMapGetter('slaReports/getAll');
const slaMetrics = useMapGetter('slaReports/getMetrics');
const slaMeta = useMapGetter('slaReports/getMeta');
const uiFlags = useMapGetter('slaReports/getUIFlags');

const pageNumber = ref(1);
const activeFilter = ref({
  from: 0,
  to: 0,
  assigned_agent_id: null,
  inbox_id: null,
  team_id: null,
  sla_policy_id: null,
  label_list: null,
});

const fetchSLAReports = ({ newPageNumber } = {}) => {
  store.dispatch('slaReports/get', {
    page: newPageNumber || pageNumber.value,
    ...activeFilter.value,
  });
};

const fetchSLAMetrics = () => {
  store.dispatch('slaReports/getMetrics', activeFilter.value);
};

const onPageChange = newPageNumber => {
  fetchSLAReports({ newPageNumber });
};

const onFilterChange = params => {
  activeFilter.value = params;
  fetchSLAReports();
  fetchSLAMetrics();
};

const downloadReports = () => {
  store.dispatch('slaReports/download', {
    fileName: generateFileName({ type: 'sla', to: activeFilter.value.to }),
    ...activeFilter.value,
  });
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('sla/get');
});

watch(
  () => [props.since, props.until],
  () => {
    fetchSLAReports();
    fetchSLAMetrics();
  }
);
</script>

<template>
  <div class="flex flex-col flex-1 gap-4">
    <div class="flex justify-end">
      <Button
        :label="t('SLA_REPORTS.DOWNLOAD_SLA_REPORTS')"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadReports"
      />
    </div>
    <SLAReportFilters
      :since="since"
      :until="until"
      @filter-change="onFilterChange"
    />
    <SLAMetrics
      :hit-rate="slaMetrics.hitRate"
      :no-of-breaches="slaMetrics.numberOfSLAMisses"
      :no-of-conversations="slaMetrics.numberOfConversations"
      :is-loading="uiFlags.isFetchingMetrics"
    />
    <SLATable
      :sla-reports="slaReports"
      :is-loading="uiFlags.isFetching"
      :current-page="Number(slaMeta.currentPage)"
      :total-count="Number(slaMeta.count)"
      @page-change="onPageChange"
    />
  </div>
</template>
